/*
 * DECIMAL-UTILS.JS - Decimal.js Configuration and Helper Functions
 *
 * SUMMARY:
 * Centralized utility file for Decimal.js configuration and helper functions
 * used across the application for exact financial calculations. Eliminates
 * code duplication and ensures consistent precision handling.
 *
 * NEW FEATURES ADDED:
 * 1. Centralized Decimal.js Configuration - Single source of truth for precision settings
 * 2. Reusable Helper Functions - toDecimal() and roundValue() for consistent use
 * 3. Financial Precision Standards - Consistent 2-decimal rounding across the app
 * 4. Code Deduplication - Eliminates repeated configuration across multiple files
 *
 * MAJOR FUNCTION ADDITIONS:
 * - configureDecimal() - Sets up Decimal.js with financial precision settings
 * - toDecimal() - Safe conversion to Decimal with null/undefined handling
 * - roundValue() - Consistent 2-decimal rounding for financial display
 *
 *  PRECISION FIXES:
 * - Percentage calculations now exact (no more 16.28999999999999%)
 * - Amount calculations precise to the cent
 * - Sum operations maintain exact precision
 * - Division operations avoid floating-point errors
 *
 * USE CASES:
 * - Invoice generation and progress claims
 * - Budget calculations and aggregations
 * - Percentage and amount calculations
 * - Financial data processing
 * - Any calculation requiring exact precision
 */

import Decimal from "decimal.js";

/**
 * Configures Decimal.js for financial calculations with optimal precision settings.
 * Should be called once at application startup.
 */
export const configureDecimal = () => {
  Decimal.set({
    precision: 20, // High precision for intermediate calculations
    rounding: Decimal.ROUND_HALF_UP, // Standard financial rounding
    toExpNeg: -7, // Don't use scientific notation for small numbers
    toExpPos: 21, // Don't use scientific notation for large numbers
  });
};

// Configure Decimal.js immediately when this module is imported
configureDecimal();

/**
 * Converts a value to a Decimal value safely, handling null, undefined, and string inputs.
 * turning a string into a Decimal value is crucial to utilize the Decimal.js library's
 * helper functions that will allow for arithmetic operations and calculations
 *
 * @param {number|string|null|undefined} value - The value to convert
 * @returns {Decimal} Decimal instance
 *
 * @example
 * toDecimal(16.29); // Returns: Decimal(16.29)
 * toDecimal("814.00"); // Returns: Decimal(814.00)
 * toDecimal(null); // Returns: Decimal(0)
 * toDecimal("$1,537.54") or toDecimal("30.03%") // Returns: Decimal(1537.54) or Decimal(0.3003)
 */

export const toDecimal = (value) => {
  // 1️⃣ Handle null, undefined, or empty string early
  if (value === null || value === undefined || value === "") {
    return new Decimal(0);
  }

  // 2️⃣ Already a Decimal → return directly
  if (Decimal.isDecimal(value)) {
    return value;
  }

  // 3️⃣ If it's a finite number → wrap directly
  if (typeof value === "number" && isFinite(value)) {
    return new Decimal(value);
  }

  // 4️⃣ Handle strings
  if (typeof value === "string") {
    let cleaned = value.trim();

    // Detect and handle percentage (e.g., "30.03%")
    const isPercent = cleaned.endsWith("%");
    if (isPercent) {
      cleaned = cleaned.replace("%", "");
    }

    // Remove non-numeric characters except digits, dot, and minus
    cleaned = cleaned.replace(/[^0-9.-]/g, "");

    // Guard against empty, invalid, or partial strings
    if (
      cleaned === "" ||
      cleaned === "-" ||
      cleaned === "." ||
      isNaN(Number(cleaned))
    ) {
      return new Decimal(0);
    }

    // Convert to Decimal and handle percent normalization
    const decimalValue = new Decimal(cleaned);
    return decimalValue;
  }

  // 5️⃣ Fallback for any other unexpected type
  return new Decimal(0);
};

/**
 * Rounds a Decimal value to 2 decimal places STRICTLY USE FOR FINANCIAL DISPLAY.
 * @deprecated
 * @param {Decimal} decimalValue - The Decimal value to round
 * @returns {number} Rounded number with 2 decimal places
 *
 * @example
 * roundValue(new Decimal(16.28999999999999)); // Returns: 16.29
 * roundValue(new Decimal(814.001)); // Returns: 814.00
 */
export const roundTwoDecimalPlace = (decimalValue) => {
  return typeof decimalValue === Decimal
    ? decimalValue.toDecimalPlaces(2).toNumber()
    : new Decimal(decimalValue).toDecimalPlaces(2).toNumber();
};

/**
 * Rounds a Decimal value to the specified number of decimal places.
 * If decimalPlaces is undefined, returns the full precision value.
 *
 * @param {Decimal|number|string} decimalValue - The value to round
 * @param {number} [decimalPlaces] - Number of decimal places to round to (optional)
 * @returns {number} Rounded number
 *
 * @example
 * roundValue(16.28999999999999, 2); // Returns: 16.29
 * roundValue(123.456); // Returns: 123.456 (full precision)
 */
export const roundValue = (decimalValue, decimalPlaces) => {
  const decimal =
    typeof decimalValue === "object" && decimalValue instanceof Decimal
      ? decimalValue
      : toDecimal(decimalValue || 0);

  return decimalPlaces !== undefined
    ? decimal.toDecimalPlaces(decimalPlaces).toNumber()
    : decimal.toNumber();
};

// Re-export Decimal for convenience
export { Decimal };
